/**
 * useInviteStaff — TanStack mutation over `POST /admin/staff`.
 *
 * Body: `{ email, role: 'root' | 'support' }`.
 * Response (201): `{ invitationId, email, role, status: 'invited' }`.
 *
 * Error envelope (tagged union):
 *   - 'duplicate' — 422 (a pending invitation for this email exists).
 *   - 'forbidden' — 403 (caller is not root).
 *   - 'error'     — anything else.
 *
 * On success, invalidates the canonical staff list key so the new
 * `invited` row appears.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../shared/api-client.js';
import type { PlatformRole } from '../../../shared/auth/admin-permissions.js';
import { STAFF_LIST_QUERY_KEY } from './use-staff-list.js';

export interface InviteStaffInput {
  readonly email: string;
  readonly role: PlatformRole;
}

export interface InviteStaffResult {
  readonly invitationId: string;
  readonly email: string;
  readonly role: PlatformRole;
  readonly status: 'invited';
}

export type InviteStaffError =
  | { readonly kind: 'duplicate' }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error' };

export interface UseInviteStaffResult {
  readonly mutateAsync: (input: InviteStaffInput) => Promise<InviteStaffResult>;
  readonly isPending: boolean;
}

export function useInviteStaff(): UseInviteStaffResult {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    InviteStaffResult,
    InviteStaffError,
    InviteStaffInput
  >({
    mutationFn: async (input) => {
      const { response, data } = await apiClient.POST('/admin/staff', {
        body: { email: input.email, role: input.role },
      });
      if (response.status === 422) {
        throw { kind: 'duplicate' } satisfies InviteStaffError;
      }
      if (response.status === 403) {
        throw { kind: 'forbidden' } satisfies InviteStaffError;
      }
      if (!response.ok || data === undefined) {
        throw { kind: 'error' } satisfies InviteStaffError;
      }
      const dto = data as {
        invitationId: string;
        email: string;
        role: PlatformRole;
        status: 'invited';
      };
      return {
        invitationId: dto.invitationId,
        email: dto.email,
        role: dto.role,
        status: dto.status,
      };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: STAFF_LIST_QUERY_KEY });
    },
  });

  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
